// EmptyState (Spec §7 / P4) — the one standard empty state. Replaces blank
// screens with guidance: what this is, why it's empty, and the single action
// that fills it. Used for first-run and no-results alike.

import type { ReactNode } from 'react'
import type { LucideIcon } from 'lucide-react'
import { Inbox } from 'lucide-react'

interface EmptyStateProps {
  icon?: LucideIcon
  title: string
  /** why it's empty + what to do about it */
  description?: string
  /** the single action that fills the state */
  action?: ReactNode
  /** optional secondary hint / link */
  secondary?: ReactNode
  /** compact variant for inline/list contexts */
  compact?: boolean
}

export default function EmptyState({ icon, title, description, action, secondary, compact }: EmptyStateProps) {
  const Icon = icon ?? Inbox
  return (
    <div
      className={`flex flex-col items-center justify-center rounded-2xl border border-dashed border-slate-200 bg-white/60 text-center ${
        compact ? 'px-6 py-8' : 'px-6 py-14'
      }`}
    >
      <span className="mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-iflow-100 text-iflow-600">
        <Icon size={26} aria-hidden />
      </span>
      <h3 className="text-lg font-semibold text-iflow-900">{title}</h3>
      {description && <p className="mt-1 max-w-md text-sm text-slate-600">{description}</p>}
      {action && <div className="mt-5">{action}</div>}
      {secondary && <div className="mt-3 text-sm text-slate-500">{secondary}</div>}
    </div>
  )
}
